import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../contexts/AuthContext";
import Sidebar from "../../UI/Sidebar";
import { LoadingProgress, ChartCard } from "../Common";
import { useSequentialLoading } from "../Common/hooks/useSequentialLoading";
import SimpleStatCard from "../Common/components/SimpleStatCard";
import UserFilter from "../Common/components/UserFilter";
import SkeletonLoader from "../../UI/SkeletonLoader";
import {
  DMPieChart,
  DMStackedChart,
  greenColor,
  orangeColor,
  redColor,
  yellowColor,
  multicolor,
} from "../../UI/DMCharts";
import Loader from "../../UI/Loader";
import ErrorMessage from "../../UI/ErrorMessage";
import OpenTasksTable from "../../UI/OpenTasksTable";
import {
  _callPrepsCS,
  _auditsCS,
  _completedDirectorAccReviewCS,
  _accountByProducts,
  _pausedProducts,
  _overdueInvoices,
  _completionOfSAItasks,
} from "../../Utils/CSInit";
import { openURLInNewTab } from "../../Utils";
import Filter from "../../UI/Filter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  faInfoCircle,
  faSync,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import DataTable from "./DataTable";
import {
  getActiveMemberships,
  getCallPreps,
  getCallPrepsLast7Days,
  getCallPrepsThisWeek,
  getCallPrepsThisMonth,
  getUpcomingGMOrderExpirations,
  getAuditsThisMonth,
  getOverdueTasks,
  getAccountsWithNoOpenActivities,
  getOverdueInvoices,
  getAccountsByProduct,
  getTasksCompletedThisWeek,
  getTasksCompletedThisMonth,
  getCompletedDirectorAccountReviews,
  getPausedProducts,
  getCompletionOfSAItasks,
  getDMOnlyReport,
  getGMOnlyReport,
  getTodaysTasks,
  getDirectorsTodaysTasks,
  getOverdueDARs,
  getCoordinatorsList,
  getAccountsWithInactiveAE,
  getConcernedAccounts,
  getHighRiskAccounts,
  getCancellingAccounts,
} from "./coordinatorsQueries";

const CoordinatorsDashboard = () => {
  const { type, owId, sidebar, hidediv } = useParams();
  const navigate = useNavigate();
  const context = useAuthContext();

  // Add render count tracking for debugging
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  // State for user filtering
  const [selectedCoordinator, setSelectedCoordinator] = useState(owId || null);
  const [coordinators, setCoordinators] = useState([]);
  const [loadingCoordinators, setLoadingCoordinators] = useState(false);

  // Effect to update selectedCoordinator when owId changes
  useEffect(() => {
    if (owId !== undefined) {
      setSelectedCoordinator(owId === "0" ? null : owId);
    }
  }, [owId]);

  // State for today's tasks (loaded separately)
  const [todaysTasks, setTodaysTasks] = useState([]);
  const [loadingTodaysTasks, setLoadingTodaysTasks] = useState(false);

  // Use refs to store stable chart data and prevent re-renders
  const chartDataRef = useRef({});
  const lastDataHashRef = useRef("");

  // Debug effect for tracking re-renders - only log when state changes
  useEffect(() => {
    console.log("🎨 CoordinatorsDashboard: Component state changed", {
      renderCount: renderCountRef.current,
      selectedCoordinator,
      contextReady: context?.ready,
      coordinatorsCount: coordinators.length,
      todaysTasksCount: todaysTasks.length,
      type,
      owId,
      sidebar,
      hidediv,
    });
  }, [
    selectedCoordinator,
    context?.ready,
    coordinators.length,
    todaysTasks.length,
    type,
    owId,
    sidebar,
    hidediv,
  ]);

  // Load coordinators list when context is ready
  useEffect(() => {
    if (!context?.ready) {
      return;
    }

    if (coordinators.length > 0) {
      return;
    }

    console.log("🔄 Loading coordinators list...");
    setLoadingCoordinators(true);
    getCoordinatorsList(context)
      .then((result) => {
        console.log("✅ Coordinators loaded:", result);
        setCoordinators(result);
      })
      .catch((error) => {
        console.error("❌ Error loading coordinators:", error);
        setCoordinators([]); // Set empty array on error
      })
      .finally(() => setLoadingCoordinators(false));
  }, [context?.ready]); // Only depend on context.ready

  // Memoize queryParams to prevent unnecessary re-renders
  const queryParams = useMemo(
    () => ({
      selectedCoordinator,
      type,
    }),
    [selectedCoordinator, type]
  );

  const {
    data,
    loading,
    error,
    progress,
    handleRetry,
    isLoading,
    isInitialized,
  } = useSequentialLoading({
    queries: {
      activeMemberships: getActiveMemberships,
      overdueTasks: getOverdueTasks,
      noOpenActivities: getAccountsWithNoOpenActivities,
      auditsThisMonth: getAuditsThisMonth,
      tasksCompletedThisWeek: getTasksCompletedThisWeek,
      tasksCompletedThisMonth: getTasksCompletedThisMonth,
      callPrepsThisWeek: getCallPrepsThisWeek,
      callPrepsThisMonth: getCallPrepsThisMonth,
      callPreps: getCallPreps,
      callPrepsLast7Days: getCallPrepsLast7Days,
      upcomingGMOrderExpirations: getUpcomingGMOrderExpirations,
      accountsByProduct: getAccountsByProduct,
      overdueInvoices: getOverdueInvoices,
      completedDirectorAccountReviews: getCompletedDirectorAccountReviews,
      pausedProducts: getPausedProducts,
      completionOfSAItasks: getCompletionOfSAItasks,
      dmOnlyReport: getDMOnlyReport,
      accountsWithInactiveAE: getAccountsWithInactiveAE,
      concernedAccounts: getConcernedAccounts,
      highRiskAccounts: getHighRiskAccounts,
      cancellingAccounts: getCancellingAccounts,
      overdueDARs: getOverdueDARs,
    },
    context: context,
    queryParams: queryParams,
  });

  // Debug logging - only log when loading state changes
  useEffect(() => {
    if (isInitialized && !isLoading) {
      console.log("✅ CoordinatorsDashboard: All queries completed", {
        dataKeys: Object.keys(data || {}),
        errorKeys: Object.keys(error || {}),
        renderCount: renderCountRef.current,
      });
    }
  }, [isInitialized, isLoading]);

  // Check if ALL data is ready
  const isAllDataReady = useMemo(() => {
    const mainDataReady = isInitialized && !isLoading;
    const allReady = mainDataReady;

    console.log("📊 Coordinators Dashboard - Data readiness check:", {
      mainDataReady,
      allReady,
      renderCount: renderCountRef.current,
    });

    return allReady;
  }, [isInitialized, isLoading]);

  const overdueTasks = [];
  const userOptionsCSC = [];

  // Initialize chart structures
  const callPrepsStructure = _callPrepsCS();
  const auditsStructure = _auditsCS();
  const completedDirectorAccReviewsStructure = _completedDirectorAccReviewCS();
  const accountByProductsStructure = _accountByProducts();
  const pausedProductsStructure = _pausedProducts();
  const overdueInvoicesStructure = _overdueInvoices();
  const completionOfSAItasksStructure = _completionOfSAItasks();
  const upcomingGMOrderExpirationsStructure = _callPrepsCS(); // Reusing same structure since it's also a stacked chart
  const darkFont = true;

  // Load today's tasks separately after main dashboard loads
  useEffect(() => {
    if (!context?.ready || !isAllDataReady) return;

    const loadTodaysTasks = async () => {
      setLoadingTodaysTasks(true);
      try {
        const tasks = await getTodaysTasks(context, selectedCoordinator);
        setTodaysTasks(tasks);
      } catch (error) {
        console.error("Error loading today's tasks:", error);
        setTodaysTasks([]);
      } finally {
        setLoadingTodaysTasks(false);
      }
    };

    const timeoutId = setTimeout(loadTodaysTasks, 500);
    return () => clearTimeout(timeoutId);
  }, [context?.ready, isAllDataReady, selectedCoordinator]);

  // Load director's today's tasks if a coordinator is selected
  useEffect(() => {
    if (!context?.ready || !isAllDataReady || !selectedCoordinator) return;

    const loadDirectorsTasks = async () => {
      try {
        const directorTasks = await getDirectorsTodaysTasks(
          context,
          selectedCoordinator
        );
        // Merge director tasks with coordinator tasks
        setTodaysTasks((prevTasks) => {
          const coordinatorTasks = prevTasks.filter((task) => !task.isDirector);
          return [...coordinatorTasks, ...directorTasks];
        });
      } catch (error) {
        console.error("Error loading director's tasks:", error);
      }
    };

    const timeoutId = setTimeout(loadDirectorsTasks, 1000);
    return () => clearTimeout(timeoutId);
  }, [context?.ready, isAllDataReady, selectedCoordinator]);

  // Stable chart data processing - only update when data actually changes
  const processedChartData = useMemo(() => {
    const currentDataHash = JSON.stringify({
      accountsByProduct: data.accountsByProduct,
      upcomingGMOrderExpirations: data.upcomingGMOrderExpirations,
      pausedProducts: data.pausedProducts,
      overdueInvoices: data.overdueInvoices,
      completionOfSAItasks: data.completionOfSAItasks,
      callPrepsThisWeek: data.callPrepsThisWeek,
      callPrepsThisMonth: data.callPrepsThisMonth,
      overdueDARs: data.overdueDARs,
      selectedCoordinator,
      type,
    });

    // Only reprocess if data actually changed
    if (
      currentDataHash === lastDataHashRef.current &&
      Object.keys(chartDataRef.current).length > 0
    ) {
      return chartDataRef.current;
    }

    lastDataHashRef.current = currentDataHash;

    // Process chart data
    const chartData = {};

    // Accounts by Product (Bar Chart)
    if (data.accountsByProduct) {
      const productData = {};
      Object.keys(data.accountsByProduct).forEach((key) => {
        if (
          key !== "breakdown" &&
          key !== "count" &&
          key !== "data" &&
          typeof data.accountsByProduct[key] === "number"
        ) {
          productData[key] = data.accountsByProduct[key];
        }
      });

      const productNames = Object.keys(productData).sort(
        (a, b) => productData[b] - productData[a]
      );

      chartData.accountsByProduct = {
        labels: productNames,
        datasets: [
          {
            label: "Number of Accounts",
            data: productNames.map((name) => productData[name]),
            backgroundColor: productNames.map((_, index) => {
              const baseColor = multicolor[index % multicolor.length];
              return `linear-gradient(135deg, ${baseColor}dd, ${baseColor}bb)`;
            }),
            borderColor: "white",
            borderWidth: 1,
          },
        ],
      };
    }

    // Stacked charts with consistent structure
    const createStackedData = (sourceData) => ({
      labels: ["Tasks"],
      datasets: [
        {
          label: "On Time",
          data: [sourceData?.Green || 0],
          backgroundColor: `linear-gradient(135deg, ${greenColor}dd, ${greenColor}bb)`,
          borderColor: "white",
          borderWidth: 1,
        },
        {
          label: "Needs Attention",
          data: [sourceData?.Yellow || 0],
          backgroundColor: `linear-gradient(135deg, ${yellowColor}dd, ${yellowColor}bb)`,
          borderColor: "white",
          borderWidth: 1,
        },
        {
          label: "Overdue",
          data: [sourceData?.Red || 0],
          backgroundColor: `linear-gradient(135deg, ${redColor}dd, ${redColor}bb)`,
          borderColor: "white",
          borderWidth: 1,
        },
      ],
    });

    if (data.upcomingGMOrderExpirations) {
      chartData.upcomingGMOrderExpirations = createStackedData(
        data.upcomingGMOrderExpirations
      );
    }

    if (data.pausedProducts) {
      chartData.pausedProducts = createStackedData(data.pausedProducts);
    }

    if (data.overdueInvoices) {
      chartData.overdueInvoices = createStackedData(data.overdueInvoices);
    }

    if (data.completionOfSAItasks) {
      chartData.completionOfSAItasks = createStackedData(
        data.completionOfSAItasks
      );
    }

    if (data.callPrepsThisWeek) {
      chartData.callPrepsThisWeek = createStackedData(data.callPrepsThisWeek);
    }

    if (data.callPrepsThisMonth) {
      chartData.callPrepsThisMonth = createStackedData(data.callPrepsThisMonth);
    }

    if (data.auditsThisMonth) {
      chartData.auditsThisMonth = createStackedData(data.auditsThisMonth);
    }

    if (data.completedDirectorAccountReviews) {
      chartData.completedDirectorAccountReviews = createStackedData(
        data.completedDirectorAccountReviews
      );
    }

    if (data.overdueDARs) {
      chartData.overdueDARs = createStackedData(data.overdueDARs);
    }

    chartDataRef.current = chartData;
    return chartData;
  }, [
    data.accountsByProduct,
    data.upcomingGMOrderExpirations,
    data.pausedProducts,
    data.overdueInvoices,
    data.completionOfSAItasks,
    data.callPrepsThisWeek,
    data.callPrepsThisMonth,
    data.auditsThisMonth,
    data.completedDirectorAccountReviews,
    data.overdueDARs,
    selectedCoordinator,
    type,
  ]);

  // Memoized event handlers
  const handleChartClick = useCallback(
    (chartType, color) => {
      // Navigate to the DataTable component with the correct path
      navigate(`/coordinatorsDashboard/data/${chartType}/${color}`);
    },
    [navigate]
  );

  const handleCoordinatorChange = useCallback((coordinatorId) => {
    setSelectedCoordinator(coordinatorId);
  }, []);

  // Show loading state
  if (!context?.ready || !isAllDataReady) {
    return (
      <div style={{ height: "100vh", overflow: "auto" }}>
        <div className="container">
          <Sidebar activeItem="coordinators" />
          <div className="content dashboard main-content">
            <div className="dashboard-header">
              <h1>Coordinators Dashboard</h1>
              <UserFilter
                coordinators={coordinators}
                selectedCoordinator={selectedCoordinator}
                onCoordinatorChange={setSelectedCoordinator}
                loading={loadingCoordinators}
                placeholder="All Coordinators"
              />
            </div>

            <div className="dashboard-main">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <Loader />
                  <div style={{ marginTop: "20px" }}>
                    Loading Coordinators Dashboard...
                  </div>
                  {isLoading && <LoadingProgress progress={progress} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state - check if any query has an error
  const hasErrors = Object.values(error).some(
    (err) => err !== null && err !== false
  );
  if (hasErrors) {
    console.error("Dashboard errors:", error);
    return (
      <div style={{ height: "100vh", overflow: "auto" }}>
        <div className="container">
          <Sidebar activeItem="coordinators" />
          <div className="content dashboard main-content">
            <div className="dashboard-header">
              <h1>Coordinators Dashboard</h1>
              <UserFilter
                coordinators={coordinators}
                selectedCoordinator={selectedCoordinator}
                onCoordinatorChange={setSelectedCoordinator}
                loading={loadingCoordinators}
                placeholder="All Coordinators"
              />
            </div>

            <div className="dashboard-main">
              <ErrorMessage
                message="Failed to load coordinators data. Please try again."
                onRetry={() => window.location.reload()}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "100vh", overflow: "auto" }}>
      <style jsx>
        {`
          .container {
            display: flex;
            min-height: 100vh;
            background-color: #f8f9fa;
          }

          .main-content {
            flex: 1;
            overflow-y: auto;
          }

          .skeleton-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }

          /* Cards Grid */
          .cards-grid {
            display: grid;
            gap: 4px !important;
            margin-bottom: 20px;
            width: 100%;
          }

          /* Mobile: 1 card per row */
          @media (max-width: 768px) {
            .cards-grid {
              grid-template-columns: 1fr !important;
            }
          }

          /* Desktop: max 4 per row, for 12 cards = 4 + 4 + 4 distribution */
          @media (min-width: 769px) {
            .cards-grid {
              grid-template-columns: repeat(4, 1fr) !important;
            }
          }

          /* Full Width Chart - Products by Account */
          .full-width-chart {
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 20px;
            margin-bottom: 24px;
            width: 100% !important;
          }

          .full-width-chart > * {
            width: 100% !important;
            min-height: 450px;
          }

          .full-width-chart canvas {
            width: 100% !important;
            height: auto !important;
          }

          /* Two Column Charts Grid - Desktop: 2 per row, Mobile: 1 per row */
          .two-col-charts-grid {
            display: grid !important;
            gap: 20px;
            margin-bottom: 24px;
            width: 100%;
          }

          .two-col-charts-grid > * {
            min-height: 400px;
            width: 100%;
          }

          /* Mobile: 1 chart per row */
          @media (max-width: 768px) {
            .two-col-charts-grid {
              grid-template-columns: 1fr !important;
            }
          }

          /* Desktop: 2 charts per row */
          @media (min-width: 769px) {
            .two-col-charts-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }

          /* Ensure all chart containers are properly sized */
          .chart-card {
            width: 100% !important;
            height: auto !important;
            display: flex !important;
            flex-direction: column !important;
          }

          .chart-card canvas {
            width: 100% !important;
            height: auto !important;
          }

          /* Open Tasks Table - Updated column widths */
          .open-tasks-table {
            margin-top: 32px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }

          .open-tasks-table-container {
            overflow-x: hidden;
            max-height: 400px;
            overflow-y: auto;
          }

          .open-tasks-table table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          .open-tasks-table th,
          .open-tasks-table td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #dee2e6;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .open-tasks-table th {
            background-color: #f8f9fa;
            font-weight: 600;
            position: sticky;
            top: 0;
            z-index: 10;
          }

          .open-tasks-table tbody tr:hover {
            background-color: #f8f9fa;
            cursor: pointer;
          }

          /* Updated Column widths - Details 30%, others equal */
          .open-tasks-table th:nth-child(1),
          .open-tasks-table td:nth-child(1) {
            width: 14%;
            white-space: nowrap;
          } /* Owner */
          .open-tasks-table th:nth-child(2),
          .open-tasks-table td:nth-child(2) {
            width: 14%;
            white-space: nowrap;
          } /* Account */
          .open-tasks-table th:nth-child(3),
          .open-tasks-table td:nth-child(3) {
            width: 14%;
            white-space: nowrap;
          } /* Subject */
          .open-tasks-table th:nth-child(4),
          .open-tasks-table td:nth-child(4) {
            width: 14%;
            white-space: nowrap;
          } /* Task Type */
          .open-tasks-table th:nth-child(5),
          .open-tasks-table td:nth-child(5) {
            width: 14%;
            white-space: nowrap;
          } /* Date */
          .open-tasks-table th:nth-child(6),
          .open-tasks-table td:nth-child(6) {
            width: 30%;
            white-space: normal;
            word-wrap: break-word;
          } /* Details */

          /* Mobile responsiveness */
          @media (max-width: 768px) {
            .open-tasks-table th,
            .open-tasks-table td {
              padding: 8px;
              font-size: 14px;
            }
          }
        `}
      </style>
      <div className="container">
        {!error.global && (
          <>
            {type ? <></> : sidebar !== "0" ? <Sidebar /> : <></>}
            <div
              className={
                type
                  ? "contentWOS dashboard main-content"
                  : "content dashboard main-content"
              }
            >
              {/* Header Section with User Filter */}
              <div style={{ padding: "20px 20px 0 20px" }}>
                <div
                  style={{
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                    flexWrap: "wrap",
                    marginLeft: "10px",
                  }}
                >
                  <UserFilter
                    users={coordinators}
                    selectedUser={selectedCoordinator}
                    onUserChange={handleCoordinatorChange}
                    loading={loadingCoordinators}
                    userType="Coordinator"
                  />
                  {/* Fallback when no coordinators */}
                  {!loadingCoordinators && coordinators.length === 0 && (
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#dc3545",
                        marginTop: "10px",
                        padding: "10px",
                        backgroundColor: "#f8d7da",
                        border: "1px solid #f5c6cb",
                        borderRadius: "4px",
                      }}
                    >
                      ⚠️ No coordinators found. Please check your Salesforce
                      connection and user roles.
                    </div>
                  )}
                </div>
              </div>

              <div style={{ padding: "0 20px 20px 20px" }}>
                {/* All Status Cards Section - Consolidated at Top */}
                <div className="cards-grid">
                  <SimpleStatCard
                    title="Active Memberships"
                    value={data.activeMemberships}
                    loading={false}
                    error={error.activeMemberships}
                    onRetry={() => handleRetry("activeMemberships")}
                    color="green"
                    tooltip="Membership Orders with Status = Activated"
                    onClick={() => handleChartClick("activeMemberships", "all")}
                    style={{ padding: "16px 12px" }}
                    summary="Customer and Customer (CA) accounts"
                  />

                  <SimpleStatCard
                    title="Overdue Tasks"
                    value={data.overdueTasks}
                    loading={false}
                    error={error.overdueTasks}
                    onRetry={() => handleRetry("overdueTasks")}
                    color={data.overdueTasks?.count > 0 ? "red" : "green"}
                    tooltip="Tasks for Active accounts with Status = Open and Date < today"
                    onClick={() =>
                      openURLInNewTab(
                        "https://gargleinc.lightning.force.com/lightning/r/Report/00OEY000000KCr32AG/view"
                      )
                    }
                    style={{ padding: "16px 12px" }}
                    summary="Past due tasks requiring attention"
                  />

                  <SimpleStatCard
                    title="Accts. No Planned Task"
                    value={data.noOpenActivities}
                    loading={false}
                    error={error.noOpenActivities}
                    onRetry={() => handleRetry("noOpenActivities")}
                    color={data.noOpenActivities?.count > 0 ? "red" : "green"}
                    tooltip="Accounts with no Tasks with Status = Open"
                    onClick={() =>
                      openURLInNewTab(
                        "https://gargleinc.lightning.force.com/lightning/r/Report/00OEY000000Jcv42AC/view"
                      )
                    }
                    style={{ padding: "16px 12px" }}
                    summary="Client accounts needing task planning"
                  />

                  <SimpleStatCard
                    title="Audits This Month"
                    value={data.auditsThisMonth}
                    loading={false}
                    error={error.auditsThisMonth}
                    onRetry={() => handleRetry("auditsThisMonth")}
                    color="green"
                    tooltip="Number of audited accounts this month"
                    onClick={() =>
                      openURLInNewTab(
                        "https://gargleinc.lightning.force.com/lightning/r/Report/00OEY000000KCqA2AW/view"
                      )
                    }
                    style={{ padding: "16px 12px" }}
                    summary="Account audits completed this month"
                  />

                  <SimpleStatCard
                    title="Tasks Completed This Week"
                    value={data.tasksCompletedThisWeek}
                    loading={false}
                    error={error.tasksCompletedThisWeek}
                    onRetry={() => handleRetry("tasksCompletedThisWeek")}
                    color="green"
                    tooltip="Tasks completed in the current week"
                    style={{ padding: "16px 12px" }}
                    summary="All task types completed this week"
                  />

                  <SimpleStatCard
                    title="Tasks Completed This Month"
                    value={data.tasksCompletedThisMonth}
                    loading={false}
                    error={error.tasksCompletedThisMonth}
                    onRetry={() => handleRetry("tasksCompletedThisMonth")}
                    color="green"
                    tooltip="Tasks completed in the current month"
                    style={{ padding: "16px 12px" }}
                    summary="All task types completed this month"
                  />

                  <SimpleStatCard
                    title="Call Preps This Week"
                    value={data.callPrepsThisWeek}
                    loading={false}
                    error={error.callPrepsThisWeek}
                    onRetry={() => handleRetry("callPrepsThisWeek")}
                    color="green"
                    tooltip="Call prep tasks completed this week"
                    onClick={() => handleChartClick("callPrepsThisWeek", "all")}
                    style={{ padding: "16px 12px" }}
                    summary="Weekly call preparation tasks"
                  />

                  <SimpleStatCard
                    title="Call Preps This Month"
                    value={data.callPrepsThisMonth}
                    loading={false}
                    error={error.callPrepsThisMonth}
                    onRetry={() => handleRetry("callPrepsThisMonth")}
                    color="green"
                    tooltip="Call prep tasks completed this month"
                    onClick={() =>
                      handleChartClick("callPrepsThisMonth", "all")
                    }
                    style={{ padding: "16px 12px" }}
                    summary="Monthly call preparation tasks"
                  />

                  {/* New Cards */}
                  <SimpleStatCard
                    title="Accounts with Inactive AE"
                    value={data.accountsWithInactiveAE?.count || 0}
                    loading={false}
                    error={error.accountsWithInactiveAE}
                    onRetry={() => handleRetry("accountsWithInactiveAE")}
                    color={
                      data.accountsWithInactiveAE?.count > 0
                        ? "yellow"
                        : "green"
                    }
                    tooltip="Active accounts with inactive Account Executives"
                    onClick={() =>
                      handleChartClick("accountsWithInactiveAE", "all")
                    }
                    style={{ padding: "16px 12px" }}
                    summary="Accounts needing AE reassignment"
                  />

                  <SimpleStatCard
                    title="Concerned"
                    value={data.concernedAccounts}
                    loading={false}
                    error={error.concernedAccounts}
                    onRetry={() => handleRetry("concernedAccounts")}
                    color={data.concernedAccounts?.color || "green"}
                    tooltip="Accounts marked as Concerned for cancellation"
                    onClick={() => handleChartClick("concernedAccounts", "all")}
                    style={{ padding: "16px 12px" }}
                    summary="Accounts requiring attention"
                  />

                  <SimpleStatCard
                    title="High Risk"
                    value={data.highRiskAccounts}
                    loading={false}
                    error={error.highRiskAccounts}
                    onRetry={() => handleRetry("highRiskAccounts")}
                    color={data.highRiskAccounts?.color || "green"}
                    tooltip="Accounts marked as High Risk for cancellation"
                    onClick={() => handleChartClick("highRiskAccounts", "all")}
                    style={{ padding: "16px 12px" }}
                    summary="Accounts at high risk of cancellation"
                  />

                  <SimpleStatCard
                    title="Cancelling"
                    value={data.cancellingAccounts}
                    loading={false}
                    error={error.cancellingAccounts}
                    onRetry={() => handleRetry("cancellingAccounts")}
                    color={data.cancellingAccounts?.color || "green"}
                    tooltip="Accounts marked as Cancelling"
                    onClick={() =>
                      handleChartClick("cancellingAccounts", "all")
                    }
                    style={{ padding: "16px 12px" }}
                    summary="Accounts in cancellation process"
                  />
                </div>

                {/* Full Width Chart - Accounts by Product */}
                <div className="full-width-chart" key="full-width">
                  <ChartCard
                    key="accountsByProduct"
                    title="Accounts by Product Distribution"
                    loading={false}
                    error={error.accountsByProduct}
                    onRetry={() => handleRetry("accountsByProduct")}
                    onClick={() => handleChartClick("accountsByProduct", "all")}
                  >
                    {processedChartData.accountsByProduct ? (
                      <DMStackedChart
                        key={`accountsByProduct-${
                          selectedCoordinator || "all"
                        }-${lastDataHashRef.current}`}
                        data={processedChartData.accountsByProduct}
                        width="100%"
                        height={400}
                        onClick={(product) =>
                          handleChartClick("accountsByProduct", product)
                        }
                        darkFont={true}
                        title="Accounts by Product Distribution"
                      />
                    ) : null}
                    <div
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#666",
                        borderTop: "1px solid #eee",
                      }}
                    >
                      Distribution of active accounts across product types
                    </div>
                  </ChartCard>
                </div>

                {/* Two Column Charts Grid - Row 2 */}
                <div className="two-col-charts-grid" key="row-2">
                  {/* Paused Products Chart */}
                  <ChartCard
                    key="pausedProducts"
                    title="Paused Products Status"
                    loading={false}
                    error={error.pausedProducts}
                    onRetry={() => handleRetry("pausedProducts")}
                    onClick={() => handleChartClick("pausedProducts", "all")}
                  >
                    {processedChartData.pausedProducts ? (
                      <DMStackedChart
                        key={`pausedProducts-${selectedCoordinator || "all"}-${
                          lastDataHashRef.current
                        }`}
                        data={processedChartData.pausedProducts}
                        width="100%"
                        height={350}
                        onClick={(color) =>
                          handleChartClick("pausedProducts", color)
                        }
                        darkFont={true}
                        title="Paused Products Status"
                      />
                    ) : null}
                    <div
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#666",
                        borderTop: "1px solid #eee",
                      }}
                    >
                      Products on pause by expiration timeline
                    </div>
                  </ChartCard>

                  {/* Overdue Invoices Chart */}
                  <ChartCard
                    key="overdueInvoices"
                    title="Overdue Invoices Status"
                    loading={false}
                    error={error.overdueInvoices}
                    onRetry={() => handleRetry("overdueInvoices")}
                    onClick={() => handleChartClick("overdueInvoices", "all")}
                  >
                    {processedChartData.overdueInvoices ? (
                      <DMStackedChart
                        key={`overdueInvoices-${selectedCoordinator || "all"}-${
                          lastDataHashRef.current
                        }`}
                        data={processedChartData.overdueInvoices}
                        width="100%"
                        height={350}
                        onClick={(color) =>
                          handleChartClick("overdueInvoices", color)
                        }
                        darkFont={true}
                        title="Overdue Invoices Status"
                      />
                    ) : null}
                    <div
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#666",
                        borderTop: "1px solid #eee",
                      }}
                    >
                      Invoices by overdue status and timeline
                    </div>
                  </ChartCard>
                </div>

                {/* Two Column Charts Grid - Row 3 */}
                <div className="two-col-charts-grid" key="row-3">
                  {/* Upcoming GM Order Expirations Chart */}
                  <ChartCard
                    key="upcomingGMOrderExpirations"
                    title="Upcoming GM Order Expirations"
                    loading={false}
                    error={error.upcomingGMOrderExpirations}
                    onRetry={() => handleRetry("upcomingGMOrderExpirations")}
                    onClick={() =>
                      handleChartClick("upcomingGMOrderExpirations", "all")
                    }
                  >
                    {processedChartData.upcomingGMOrderExpirations ? (
                      <DMStackedChart
                        key={`upcomingGMOrderExpirations-${
                          selectedCoordinator || "all"
                        }-${lastDataHashRef.current}`}
                        data={processedChartData.upcomingGMOrderExpirations}
                        width="100%"
                        height={350}
                        onClick={(color) =>
                          handleChartClick("upcomingGMOrderExpirations", color)
                        }
                        darkFont={true}
                        title="Upcoming GM Order Expirations"
                      />
                    ) : null}
                    <div
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#666",
                        borderTop: "1px solid #eee",
                      }}
                    >
                      GM orders by time until expiration
                    </div>
                  </ChartCard>

                  {/* SAI Tasks Completion Chart */}
                  <ChartCard
                    key="completionOfSAItasks"
                    title="SAI Tasks Completion"
                    loading={false}
                    error={error.completionOfSAItasks}
                    onRetry={() => handleRetry("completionOfSAItasks")}
                    onClick={() =>
                      handleChartClick("completionOfSAItasks", "all")
                    }
                  >
                    {processedChartData.completionOfSAItasks ? (
                      <DMStackedChart
                        key={`completionOfSAItasks-${
                          selectedCoordinator || "all"
                        }-${lastDataHashRef.current}`}
                        data={processedChartData.completionOfSAItasks}
                        width="100%"
                        height={350}
                        onClick={(color) =>
                          handleChartClick("completionOfSAItasks", color)
                        }
                        darkFont={true}
                        title="SAI Tasks Completion"
                      />
                    ) : null}
                    <div
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#666",
                        borderTop: "1px solid #eee",
                      }}
                    >
                      Strategic Account Initiative task completion status
                    </div>
                  </ChartCard>
                </div>

                {/* Two Column Charts Grid - Row 4 */}
                <div className="two-col-charts-grid" key="row-4">
                  {/* Overdue DARs Chart */}
                  <ChartCard
                    key="overdueDARs"
                    title="Overdue Director Account Reviews"
                    loading={false}
                    error={error.overdueDARs}
                    onRetry={() => handleRetry("overdueDARs")}
                    onClick={() => handleChartClick("overdueDARs", "all")}
                  >
                    {processedChartData.overdueDARs ? (
                      <DMStackedChart
                        key={`overdueDARs-${selectedCoordinator || "all"}-${
                          lastDataHashRef.current
                        }`}
                        data={processedChartData.overdueDARs}
                        width="100%"
                        height={350}
                        onClick={(color) =>
                          handleChartClick("overdueDARs", color)
                        }
                        darkFont={true}
                        title="Overdue Director Account Reviews"
                      />
                    ) : null}
                    <div
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#666",
                        borderTop: "1px solid #eee",
                      }}
                    >
                      Accounts by DAR status: Green (≤90 days), Yellow (91-120
                      days), Red (&gt;120 days)
                    </div>
                  </ChartCard>

                  {/* Placeholder for future chart */}
                  <div style={{ minHeight: "350px" }}></div>
                </div>

                {/* Today's Tasks Table */}
                {todaysTasks && todaysTasks.length > 0 && (
                  <div className="open-tasks-table">
                    <div
                      style={{
                        padding: "20px 20px 10px 20px",
                        backgroundColor: "#f8f9fa",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      <h4 style={{ margin: 0, color: "#333" }}>
                        Today's Tasks{" "}
                        {selectedCoordinator
                          ? `for ${
                              coordinators.find(
                                (c) => c.id === selectedCoordinator
                              )?.name || "Selected Coordinator"
                            }`
                          : ""}
                      </h4>
                      <p
                        style={{
                          margin: "5px 0 0 0",
                          color: "#666",
                          fontSize: "14px",
                        }}
                      >
                        {selectedCoordinator
                          ? "Showing today's tasks for the selected coordinator and their director"
                          : "Showing today's tasks for all coordinators"}
                        . Click on any task to open in Salesforce.
                      </p>
                    </div>

                    {/* Group tasks by user */}
                    {(() => {
                      const tasksByUser = {};
                      todaysTasks.forEach((task) => {
                        if (!tasksByUser[task.Owner]) {
                          tasksByUser[task.Owner] = [];
                        }
                        tasksByUser[task.Owner].push(task);
                      });

                      return Object.entries(tasksByUser).map(
                        ([userName, userTasks], userIndex) => (
                          <div key={userName}>
                            <div
                              style={{
                                padding: "10px 20px",
                                backgroundColor: userTasks[0]?.isDirector
                                  ? "#dc3545"
                                  : "#2b5597",
                                color: "white",
                                marginTop: userIndex === 0 ? "0" : "10px",
                                fontWeight: "600",
                                textTransform: "uppercase",
                              }}
                            >
                              {userName} - <b>{userTasks.length}</b> Tasks
                              {userTasks[0]?.isDirector && " (Director)"}
                            </div>
                            <div className="open-tasks-table-container">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Account</th>
                                    <th>Subject</th>
                                    <th>Task Type</th>
                                    <th>Date</th>
                                    <th>Details</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {userTasks.map((task, index) => (
                                    <tr
                                      key={index}
                                      onClick={() => {
                                        const salesforceUrl = `https://gargleinc.lightning.force.com/lightning/r/Task/${task.Id}/view`;
                                        window.open(salesforceUrl, "_blank");
                                      }}
                                      style={{
                                        backgroundColor: task.isDirector
                                          ? "#fff5f5"
                                          : "transparent",
                                        borderLeft: task.isDirector
                                          ? "4px solid #dc3545"
                                          : "none",
                                      }}
                                    >
                                      <td>{task.AccountName}</td>
                                      <td>{task.Subject}</td>
                                      <td>{task.TaskType}</td>
                                      <td>
                                        {task.Date
                                          ? new Date(
                                              task.Date
                                            ).toLocaleDateString()
                                          : "-"}
                                      </td>
                                      <td>{task.Details}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CoordinatorsDashboard;
